import React from 'react';
import useAsync from 'react-use/lib/useAsync';
import {
  useEntity,
  EntityProvider,
  catalogApiRef,
} from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import { EntityApiDefinitionCard } from '@backstage/plugin-api-docs';
import { Progress, ResponseErrorPanel } from '@backstage/core-components';
import Grid from '@material-ui/core/Grid';
import type { Entity } from '@backstage/catalog-model';

// providesApis in a spec is a mix of stringified refs ("name",
// "namespace/name", "api:namespace/name") and object refs. We
// normalise both to a stringified ref before calling the catalog.
type ProvidedApiRef =
  | string
  | { kind?: string; namespace?: string; name: string };

// Renders the full API definition (Swagger UI for OpenAPI, GraphQL Voyager
// for GraphQL, etc.) of every API entity the current Component provides.
//
// EntityApiDefinitionCard reads its target from context via useEntity(),
// so we wrap each fetched ApiEntity in an EntityProvider before rendering.
// That's the same trick @backstage/plugin-catalog-graph uses for
// cross-entity cards.
//
// Returns nothing when the Component provides no APIs — nothing gets
// rendered on the tab, no error, no empty box.
export const EntityProvidedApiInlineDefinitions = () => {
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);

  const providesApis = (entity.spec?.providesApis ?? []) as ProvidedApiRef[];

  const {
    loading,
    error,
    value: apis,
  } = useAsync(async () => {
    if (providesApis.length === 0) return [] as Entity[];
    const defaultNs = entity.metadata.namespace || 'default';
    const refs: string[] = providesApis.map(r => {
      if (typeof r === 'string') {
        // "name" or "namespace/name" — normalise to a stringified ref
        if (r.includes(':')) return r; // already "kind:namespace/name"
        const [ns, name] = r.includes('/') ? r.split('/') : [defaultNs, r];
        return `api:${ns}/${name}`;
      }
      // EntityRef object shape
      return `${r.kind ?? 'api'}:${r.namespace ?? defaultNs}/${r.name}`;
    });
    const response = await catalogApi.getEntitiesByRefs({ entityRefs: refs });
    return response.items.filter((e): e is Entity => Boolean(e));
  }, [providesApis.map(String).join(','), entity.metadata.namespace]);

  if (loading) return <Progress />;
  if (error) return <ResponseErrorPanel error={error} />;
  if (!apis || apis.length === 0) return null;

  return (
    <>
      {apis.map(api => (
        <Grid
          item
          xs={12}
          key={`${api.metadata.namespace ?? 'default'}/${api.metadata.name}`}
        >
          <EntityProvider entity={api}>
            <EntityApiDefinitionCard />
          </EntityProvider>
        </Grid>
      ))}
    </>
  );
};

export default EntityProvidedApiInlineDefinitions;
